import { numberUtil } from '@xtnd/web/shared';
import { AppealStatus } from '@repo/types';
import { AppealItem } from '../../types';

interface AppealFeeAdjustmentsProps {
  appeal: AppealItem;
}

export const AppealFeeAdjustments = ({ appeal }: AppealFeeAdjustmentsProps) => {
  const FeeItem = ({ label, value }: { label: string; value: number | null | undefined }) => (
    <div className="flex justify-between items-center gap-9">
      <span className="text-xs text-xtnd-dark dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
        {numberUtil.formatCurrency(value ?? 0)}
      </span>
    </div>
  );

  const TotalItem = ({ label, value }: { label: string; value: number }) => (
    <div className="flex justify-between items-center pt-2 border-t border-xtnd-dark dark:border-gray-600 gap-2">
      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</span>
      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
        {numberUtil.formatCurrency(value)}
      </span>
    </div>
  );

  // Group 1: Prompt Payment, Bulk Discount, Administrative Fee
  const group1Total =
    ((appeal.promptPayment ?? 0) + (appeal.approvedPromptPayment ?? 0)) + (appeal.bulkDiscount ?? 0) + (appeal.administrativeFee ?? 0);

  // Group 2: Expenses Cut, Fees Cut
  const group2Total = (appeal.expensesCut ?? 0) + (appeal.feesCut ?? 0);

  // Group 3: Appealed Fees, Appealed Expenses
  const group3Total = (appeal.appealedFees ?? 0) + (appeal.appealedExpenses ?? 0);

  // Group 4: Approved Fees, Approved Expenses (only if status is Approved)
  const group4Total = (appeal.approvedFees ?? 0) + (appeal.approvedExpenses ?? 0);
  const isApproved = appeal.appealStatus === AppealStatus.APPEAL_APPROVED;

  return (
    <div className="flex gap-6 h-full items-stretch justify-between">
      {/* Group 1: Discounts */}
      <div className="flex flex-1 flex-col space-y-1">
        <div className="flex-1 space-y-1">
          <FeeItem label="Prompt Payment" value={((appeal.promptPayment ?? 0) + (appeal.approvedPromptPayment ?? 0))} />
          <FeeItem label="Bulk Discount" value={appeal.bulkDiscount} />
          <FeeItem label="Admin Fee" value={appeal.administrativeFee} />
        </div>
        <TotalItem label="Total Discounts" value={group1Total} />
      </div>

      {/* Group 2: Cuts */}
      <div className="flex flex-1 flex-col space-y-1">
        <div className="flex-1 space-y-1">
          <FeeItem label="Fees Cut" value={appeal.feesCut} />
          <FeeItem label="Expenses Cut" value={appeal.expensesCut} />
        </div>
        <TotalItem label="Client Refused" value={group2Total} />
      </div>

      {/* Group 3: Appealed */}
      <div className="flex flex-1 flex-col space-y-1">
        <div className="flex-1 space-y-1">
          <FeeItem label="Appealed Fees" value={appeal.appealedFees} />
          <FeeItem label="Appealed Expenses" value={appeal.appealedExpenses} />
        </div>
        <TotalItem label="Total Appealed" value={group3Total} />
      </div>

      {/* Group 4: Approved (only shown if status is Approved) */}
      {isApproved && (
        <div className="flex flex-1 flex-col space-y-1">
          <div className="flex-1 space-y-1">
            <FeeItem label="Approved Fees" value={appeal.approvedFees} />
            <FeeItem label="Approved Expenses" value={appeal.approvedExpenses} />
          </div>
          <TotalItem label="Total Approved" value={group4Total} />
        </div>
      )}
    </div>
  );
};
